import { useEffect, useMemo, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import { cn } from '@apps/web/lib/utils';
import { type AxisCareServiceRateInfo } from '@libraries/typescript/api_client';

type AxisCareServiceRateSelectorProps = {
    readonly rateInfo: AxisCareServiceRateInfo | undefined;
    readonly isLoading?: boolean;
    readonly error?: unknown;
    readonly disabled?: boolean;
    readonly onRateChange: (serviceRateId: string | null) => void;
    readonly hasError?: boolean;
};

export function AxisCareServiceRateSelector({
    rateInfo,
    isLoading,
    error,
    disabled,
    onRateChange,
    hasError,
}: AxisCareServiceRateSelectorProps) {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const rates = useMemo(
        () => rateInfo?.service_rates ?? [],
        [rateInfo?.service_rates],
    );

    const selectedRate = useMemo(
        () => rates.find((r) => r.id === selectedId) ?? null,
        [rates, selectedId],
    );

    // Preselect the rate already on the AxisCare visit, so assigning a
    // caregiver preserves it. The client-level default is only correct for a
    // visit that carries no rate of its own — seeding from it reprices every
    // visit whose rate the coordinator overrode in AxisCare.
    const initialRateId =
        rateInfo?.visit_service_rate_id ??
        rateInfo?.default_service_rate_id ??
        null;

    useEffect(() => {
        const canPreselectRate =
            Boolean(rateInfo?.visit_service_rate_id) || rates.length > 0;
        if (initialRateId && !selectedId && canPreselectRate) {
            setSelectedId(initialRateId);
            onRateChange(initialRateId);
        }
    }, [
        initialRateId,
        selectedId,
        rates.length,
        rateInfo?.visit_service_rate_id,
        onRateChange,
    ]);

    // A visit can carry a rate that the client's service definitions don't
    // list, so name it rather than render an empty box over a live selection.
    const displayValue = selectedRate
        ? `${selectedRate.code} - ${selectedRate.description}`
        : selectedId
          ? 'Current visit rate'
          : undefined;

    return (
        <div className="mt-2 flex min-w-0 flex-1 flex-col gap-1 text-xs">
            <span className="text-muted-foreground text-xs font-medium">
                Service Rate
            </span>
            <Combobox
                open={isOpen}
                onOpenChange={setIsOpen}
                displayValue={displayValue}
                placeholder={
                    isLoading
                        ? 'Loading rates...'
                        : error
                          ? 'Error loading rates'
                          : rates.length === 0
                            ? 'No rates available'
                            : 'Select service rate'
                }
                disabled={disabled || isLoading || rates.length === 0}
                searchValue={search}
                onSearchValueChange={setSearch}
                searchPlaceholder="Search service rates..."
                triggerClassName={cn(
                    'text-left overflow-hidden',
                    hasError &&
                        'border-destructive focus-visible:ring-destructive',
                )}
                contentClassName="w-80 p-1"
                contentSide="bottom"
                contentSideOffset={8}
                commandListProps={{ className: 'max-h-64' }}
            >
                <CommandEmpty className="text-muted-foreground py-6 text-center text-xs">
                    No service rates found
                </CommandEmpty>
                {rates.map((rate) => (
                    <CommandItem
                        key={rate.id}
                        value={`${rate.code} ${rate.description}`}
                        onSelect={() => {
                            setSelectedId(rate.id);
                            setIsOpen(false);
                            onRateChange(rate.id);
                        }}
                        className="py-2 text-left text-sm"
                    >
                        {rate.code} - {rate.description}
                    </CommandItem>
                ))}
            </Combobox>
            {hasError ? (
                <p className="text-destructive text-xs">
                    Select a service rate to assign this shift.
                </p>
            ) : null}
        </div>
    );
}
